type ApiErrorResponse = {
  status?: number;
  data?: { message?: string; error?: string } | string;
};

type ApiErrorLike = {
  response?: ApiErrorResponse;
  message?: string;
};

const getStatusCode = (error: unknown): number | null => {
  const status = (error as ApiErrorLike)?.response?.status;
  return typeof status === 'number' ? status : null;
};

const getDetailMessage = (error: unknown): string | null => {
  const err = error as ApiErrorLike;
  const responseData = err?.response?.data;

  if (typeof responseData === 'string') {
    return responseData;
  }

  if (responseData && typeof responseData === 'object') {
    if (typeof responseData.message === 'string' && responseData.message.trim()) {
      return responseData.message.trim();
    }
    if (typeof responseData.error === 'string' && responseData.error.trim()) {
      return responseData.error.trim();
    }
  }

  if (typeof err?.message === 'string' && err.message.trim()) {
    return err.message.trim();
  }

  return null;
};

/**
 * Formats an error message for user-facing surfaces, appending the API status
 * code whenever it is available and including any backend-provided details.
 */
export const formatApiErrorMessage = (
  error: unknown,
  fallbackMessage: string
): string => {
  const status = getStatusCode(error);
  const detail = getDetailMessage(error);
  const statusSuffix = status ? ` (Status ${status})` : '';
  const detailSuffix = detail ? `: ${detail}` : '';
  return `${fallbackMessage}${statusSuffix}${detailSuffix}`;
};

/**
 * Logs API errors with consistent context + status code formatting.
 */
export const logApiError = (context: string, error: unknown): void => {
  const status = getStatusCode(error);
  const statusSuffix = status ? ` (Status ${status})` : '';
  // Preserve the original error object for stack/message inspection.
  console.error(`[ServiceApp] ${context}${statusSuffix}`, error);
};

export const getApiStatusCode = getStatusCode;
